import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BRAIN_ASSET_SETS, GameShell, randomItem, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;

const createSourceRound = (level = 1, excludedSetId = '') => {
    const difficultyIndex = level - 1;
    const availableSets = BRAIN_ASSET_SETS.filter((set) => set.id !== excludedSetId);
    const assetSet = randomItem(availableSets.length ? availableSets : BRAIN_ASSET_SETS);
    const count = Math.min(6, 4 + Math.floor(difficultyIndex / 4));
    const items = shuffle(assetSet.items).slice(0, count);
    const cueIndex = Math.floor(Math.random() * items.length);

    return {
        assetSet,
        cueIndex,
        id: `${Date.now()}-${Math.random()}`,
        items,
        previewMs: Math.max(1250, 2050 - difficultyIndex * 70),
    };
};

function SourceBindGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('source-bind', initialLevel);
    const timerRef = useRef(null);
    const [round, setRound] = useState(() => createSourceRound(startingLevel));
    const [phase, setPhase] = useState('preview');
    const [choice, setChoice] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = choice !== null && choice === round.cueIndex;

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const beginRound = useCallback((nextStreak = 0, excludedSetId = '') => {
        clearTimer();
        const nextLevel = normalizeBrainGameLevel('source-bind', startingLevel + nextStreak);
        const nextRound = createSourceRound(nextLevel, excludedSetId);
        setRound(nextRound);
        setChoice(null);
        setPhase('preview');
        timerRef.current = window.setTimeout(() => setPhase('answer'), nextRound.previewMs);
    }, [clearTimer]);

    useEffect(() => {
        timerRef.current = window.setTimeout(() => setPhase('answer'), round.previewMs);
        return clearTimer;
    }, []);

    const chooseCell = (index) => {
        if (phase !== 'answer' || choice !== null) {
            return;
        }

        const isCorrect = index === round.cueIndex;
        const nextStreak = isCorrect ? streak + 1 : 0;
        setChoice(index);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setPhase('feedback');
        timerRef.current = window.setTimeout(
            () => beginRound(nextStreak, round.assetSet.id),
            isCorrect ? 560 : 820
        );
    };

    const cue = round.items[round.cueIndex];
    const visibleItems = phase === 'preview' || phase === 'feedback';
    const answerClass = useMemo(() => (correct ? 'good' : choice !== null ? 'miss' : 'ready'), [choice, correct]);

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: normalizeBrainGameLevel('source-bind', startingLevel + streak), score, streak }}
            tone={answerClass}
        >
            <div className="UFOV-brain-games__source-game">
                <div
                    className="UFOV-brain-games__transient-slot is-source-cue"
                    aria-hidden={phase === 'preview'}
                >
                    {phase !== 'preview' ? (
                        <div className="UFOV-brain-games__source-cue" style={{ '--source-tone': round.assetSet.tone }}>
                            <img alt="" draggable="false" src={cue.src} />
                        </div>
                    ) : null}
                </div>
                <div className={`UFOV-brain-games__source-grid is-${round.items.length}`} aria-label={copy.objectLocationBoard}>
                    {round.items.map((item, index) => (
                        <button
                            aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                            className={`${choice === index ? (index === round.cueIndex ? 'is-correct' : 'is-wrong') : ''} ${choice !== null && index === round.cueIndex ? 'is-answer' : ''}`.trim()}
                            disabled={phase !== 'answer'}
                            key={`${round.id}-${item.id}`}
                            onClick={() => chooseCell(index)}
                            style={{ '--source-tone': round.assetSet.tone }}
                            type="button"
                        >
                            {visibleItems ? <img alt="" draggable="false" src={item.src} /> : null}
                        </button>
                    ))}
                </div>
            </div>
        </GameShell>
    );
}

export default SourceBindGame;
